import React from "react";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import OpacityIcon from "@mui/icons-material/Opacity";
import SpeedIcon from "@mui/icons-material/Speed";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import BoltIcon from "@mui/icons-material/Bolt";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CircularGauge from "./CircularGauge";
import PressureGauge from "../PressureGauge";

function iconForChannel(channelKey, channel, displayColor) {
  const key = (channel?.measurement_key || channelKey || "").toLowerCase();
  const type = channel?.channel_type;
  if (key.includes("fuel")) return <LocalGasStationIcon sx={{ color: displayColor }} />;
  if (key.includes("coolant") || key.includes("temperature") || key.includes("temp")) return <DeviceThermostatIcon sx={{ color: displayColor }} />;
  if (key.includes("voltage") || key.includes("electrical")) return <BoltIcon sx={{ color: displayColor }} />;
  if (key.includes("speed") || key.includes("rpm") || key.includes("engine")) return <SpeedIcon sx={{ color: displayColor }} />;
  if (type === "vehicle_diagnostic") return <DirectionsCarIcon sx={{ color: displayColor }} />;
  if (type === "volume") return <OpacityIcon sx={{ color: displayColor }} />;
  return <HelpOutlineIcon sx={{ color: displayColor }} />;
}

function formatValue(value) {
  if (value == null || value === "") return "--";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;
  return numericValue.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
}

const ChannelGauge = ({ channelKey, channel, compact = false, displayColor }) => {
  if (!channel) return null;

  const theme = useTheme();

  const label = channel.channel_name || channel.label || channelKey;
  const quality = channel.quality || "good";
  const value = channel.display_value ?? channel.current_value ?? channel.volume_level;
  const unit = channel.display_unit || channel.canonical_unit || "";
  const qualityColor = quality === "stale" ? "warning" : quality === "invalid" ? "error" : "success";

  if (!compact && channel.channel_type === "pressure") {
    return (
      <MDBox display="flex" flexDirection="column" alignItems="center" gap={0.5}>
        <PressureGauge
          value={channel.current_value ?? 0}
          scaleMin={-15}
          scaleMax={100}
          size={220}
          displayLabel={label}
          displayColor={displayColor || (theme.palette[qualityColor]?.main ?? theme.palette.success.main)}
        />
        <MDTypography variant="caption" color="text">
          {label} ({channel.canonical_unit || "psi"}) {channel.mode ? ` | ${channel.mode}` : ""}
        </MDTypography>
      </MDBox>
    );
  }

  if (!compact && channel.channel_type === "percent_full") {
    return <CircularGauge value={channel.current_value ?? 0} unit="%" label={label} quality={quality} colorMode={displayColor ||"level"} />;
  }

  return (
    <MDBox
      sx={({ palette, borders, functions: { rgba } }) => ({
        backgroundColor: rgba(palette.text.main, 0.04),
        border: `${borders.borderWidth[1]} solid ${rgba(palette.text.main, 0.1)}`,
        borderRadius: borders.borderRadius.lg,
        padding: "14px",
        minHeight: compact ? 116 : 150,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      })}
    >
      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
        <MDBox display="flex" fontSize="1.2rem" sx={{ color: "text.secondary" }}>
          {iconForChannel(channelKey, channel, displayColor)}
        </MDBox>
      </MDBox>
      <MDTypography variant={compact ? "h5" : "h4"} fontWeight="bold" lineHeight={1.15}>
        {formatValue(value)}{unit ? ` ${unit}` : ""}
      </MDTypography>
      <MDTypography variant="caption" color="text" title={label} sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {label}
      </MDTypography>
    </MDBox>
  );
};

export default ChannelGauge;
